import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, money, formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useCart } from "@/context/CartContext";
import { MediaTypeBadges, StatusBadge, IMAGE_BADGES } from "@/components/Badges";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Lock, ArrowRight, Images, ShoppingCart, Check, ChevronRight } from "lucide-react";

const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "";
const fmtDates = (a, b) => a && b ? `${fmtDate(a)} – ${fmtDate(b)}` : fmtDate(a) || fmtDate(b);

const CATEGORY = {
  dance_show: "Dance Show",
  dance_competition: "Competition",
  event: "Event",
};

const CAT_PLURAL = {
  dance_show: "Dance Shows",
  dance_competition: "Competitions",
  event: "Events",
};

export default function EventPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add, has } = useCart();
  const [event, setEvent] = useState(null); // null loading, false not found
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [sectionId, setSectionId] = useState("");
  const [perfId, setPerfId] = useState("");

  const load = () =>
    api.get(`/events/${id}`).then(({ data }) => setEvent(data)).catch(() => setEvent(false));
  useEffect(() => { load(); }, [id]);

  const unlock = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${id}/unlock`, { password: pw });
      setEvent(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (event === null) {
    return (
      <div className="min-h-screen bg-[#0a0a0c]"><Navbar />
        <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#ff2a5f]" /></div>
      </div>
    );
  }
  if (event === false) {
    return (
      <div className="min-h-screen bg-[#0a0a0c]"><Navbar />
        <div className="py-32 text-center text-zinc-400">This event was not found.</div>
      </div>
    );
  }

  const fields = event.fields || {};
  const isComp = event.category === "dance_competition" && (event.sections || []).length > 0;
  const badgeTypes = (event.storefront_badges && event.storefront_badges.length)
    ? event.storefront_badges
    : [...(event.content_types || []), ...(event.livestream ? ["livestream"] : []), ...(event.delivery_download ? ["download"] : [])];
  const imgBadge = IMAGE_BADGES[event.image_badge];
  const deliveryBadges = [];
  if (event.delivery_usb) deliveryBadges.push({ k: "usb", l: event.usb_fee > 0 ? `USB +${money(event.usb_fee)}` : "USB" });

  const sectionPerfs = (event.performances || []).filter((p) => (p.section_id || "") === sectionId);
  const selPerf = (event.performances || []).find((p) => p.id === perfId);
  const perfMedia = (event.media || []).filter((m) => m.performance_id === perfId);

  const addMedia = (m, perf) => {
    const sec = (event.sections || []).find((s) => s.id === (perf?.section_id || ""));
    const numberEnabled = event.fields?.number?.enabled;
    add({
      media_item_id: m.id, title: m.title, price: m.price, thumbnail_url: m.thumbnail_url,
      content_type: m.content_type || (m.media_types || [])[0] || "",
      delivery_download: event.delivery_download, delivery_usb: event.delivery_usb,
      event_id: event.id, event_name: event.name, category: event.category, fields: event.fields,
      performance_mode: "manual", performances: [],
      performance_name: event.fields?.name?.enabled ? (perf?.name || "") : "",
      performance_studio: event.fields?.studio?.enabled ? (perf?.studio || "") : "",
      performance_number: numberEnabled ? `${(sec?.prefix) || ""}${perf?.number || ""}` : "",
    });
    toast.success("Added to cart");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100">
      <Navbar />
      <div className="border-b border-zinc-800/60 bg-[#0a0a0c]/60">
        <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
          <nav data-testid="event-breadcrumb" className="flex items-center gap-1.5 text-sm text-zinc-400">
            <button onClick={() => navigate("/")} className="transition-colors hover:text-white">Store</button>
            <ChevronRight className="h-3.5 w-3.5 text-zinc-600" />
            <span data-testid="breadcrumb-category" className="font-medium text-zinc-200">{CAT_PLURAL[event.category] || "Events"}</span>
          </nav>
        </div>
      </div>
      {/* Header */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        {event.thumbnail_url && (
          <div className="absolute inset-0">
            <img src={event.thumbnail_url} alt={event.name} className="h-full w-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-[#0a0a0c]/80 to-[#0a0a0c]/40" />
          </div>
        )}
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-start">
            <div>
              {event.livestream && (
                <div className="mb-3 inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/20 px-3 py-1 font-mono-x text-xs uppercase tracking-widest text-red-200">Livestream</div>
              )}
              <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">{event.name}</h1>
              {(event.start_date || event.end_date) && (
                <div data-testid="event-dates" className="mt-2 text-sm font-medium text-zinc-400">{fmtDates(event.start_date, event.end_date)}</div>
              )}
              {event.description && <div data-testid="event-description" className="prose-invert mt-4 max-w-2xl text-base text-zinc-300 [&_a]:text-rose-400 [&_a]:underline [&_h1]:font-display [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-bold [&_h3]:font-semibold [&_strong]:text-white" dangerouslySetInnerHTML={{ __html: event.description }} />}
              {(badgeTypes.length > 0 || deliveryBadges.length > 0) && (
                <div className="mt-5 flex flex-wrap gap-2" data-testid="event-badges">
                  <MediaTypeBadges types={badgeTypes} testidPrefix={`ev-content-${event.id}`} />
                  {deliveryBadges.map((b) => (
                    <span key={b.k} data-testid={`delivery-badge-${b.k}`} className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-900/60 px-2.5 py-0.5 text-xs font-medium text-zinc-300">{b.l}</span>
                  ))}
                </div>
              )}
            </div>
            {event.cover_image_url && (
              <div className="relative w-full overflow-hidden rounded-xl border border-zinc-800 lg:w-80">
                {imgBadge && <div className="absolute left-3 top-3"><span data-testid="event-image-badge" className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${imgBadge.cls}`}>{imgBadge.label}</span></div>}
                <img data-testid="event-cover" src={event.cover_image_url} alt={event.name} className="h-full w-full object-cover" />
              </div>
            )}
          </div>
          {(event.gallery || []).length > 0 && (
            <div data-testid="event-gallery" className="mt-8 grid grid-flow-col auto-cols-[minmax(140px,1fr)] gap-3 overflow-x-auto pb-2">
              {event.gallery.slice(0, 6).map((g, i) => (
                <img key={i} data-testid={`gallery-img-${i}`} src={g} alt={`${event.name} ${i + 1}`} className="aspect-[4/3] w-full rounded-lg border border-zinc-800 object-cover" />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {!event.locked && (
          <h2 data-testid="available-media-heading" className="mb-6 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">Available Media</h2>
        )}
        {event.locked ? (
          <div data-testid="event-lock" className="mx-auto max-w-sm space-y-5 rounded-2xl border border-zinc-800 bg-zinc-900/70 p-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#ff2a5f]/15 text-[#ff2a5f]"><Lock className="h-6 w-6" /></div>
            <p className="text-sm text-zinc-400">This event is password-protected. Enter the password to view its description and media.</p>
            <Input data-testid="event-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && unlock()} placeholder="Event password" className="border-zinc-700 bg-zinc-950 text-white" />
            <Button data-testid="event-unlock" onClick={unlock} disabled={busy} className="w-full bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock event"}
            </Button>
          </div>
        ) : !event.media || event.media.length === 0 ? (
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 py-20 text-center text-zinc-500">
            <Images className="mx-auto mb-3 h-8 w-8" /> Media for this event is coming soon.
          </div>
        ) : isComp ? (
          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs uppercase tracking-wide text-zinc-500">Section</label>
                <Select value={sectionId || "none"} onValueChange={(v) => { setSectionId(v === "none" ? "" : v); setPerfId(""); }}>
                  <SelectTrigger data-testid="comp-section-select" className="border-zinc-700 bg-zinc-950 text-white"><SelectValue placeholder="Select a section" /></SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                    <SelectItem value="none">— Select a section —</SelectItem>
                    {(event.sections || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-xs uppercase tracking-wide text-zinc-500">Performance</label>
                <Select value={perfId || "none"} onValueChange={(v) => setPerfId(v === "none" ? "" : v)} disabled={!sectionId}>
                  <SelectTrigger data-testid="comp-perf-select" className="border-zinc-700 bg-zinc-950 text-white disabled:opacity-50"><SelectValue placeholder="Select a performance" /></SelectTrigger>
                  <SelectContent className="max-h-72 border-zinc-800 bg-zinc-900 text-white">
                    <SelectItem value="none">— Select a performance —</SelectItem>
                    {sectionPerfs.map((p) => <SelectItem key={p.id} value={p.id}>{p.index} · {p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!perfId ? (
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 py-16 text-center text-zinc-500">
                Choose a section and performance to see available media.
              </div>
            ) : perfMedia.length === 0 ? (
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 py-16 text-center text-zinc-500">
                Media for this performance is coming soon.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                {perfMedia.map((m) => (
                  <div key={m.id} data-testid={`event-media-${m.id}`} className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/80 transition-all hover:-translate-y-1 hover:border-rose-500/50">
                    <div className="relative aspect-[16/10] bg-zinc-950">
                      {m.thumbnail_url && <img src={m.thumbnail_url} alt={m.title} className="h-full w-full object-cover" />}
                      <div className="absolute left-3 top-3"><StatusBadge status={m.order_status} /></div>
                    </div>
                    <div className="space-y-3 p-5">
                      <MediaTypeBadges types={m.media_types} testidPrefix={`ev-badge-${m.id}`} />
                      <h3 className="font-display text-lg font-bold text-white">{m.title}</h3>
                      <div className="flex items-center justify-between pt-2">
                        <span className="font-display text-xl font-extrabold text-white">{money(m.price)}</span>
                        {has(m.id) ? (
                          <Button data-testid={`in-cart-${m.id}`} onClick={() => navigate("/checkout")} variant="outline" className="border-emerald-500/40 bg-transparent text-emerald-400 hover:bg-emerald-500/10">
                            <Check className="mr-1.5 h-4 w-4" /> In cart
                          </Button>
                        ) : (
                          <Button data-testid={`event-buy-${m.id}`} onClick={() => addMedia(m, selPerf)} disabled={!m.available} className="bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50] disabled:opacity-40">
                            <ShoppingCart className="mr-1.5 h-4 w-4" /> Add
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {event.media.map((m) => (
              <div key={m.id} data-testid={`event-media-${m.id}`} className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/80 transition-all hover:-translate-y-1 hover:border-rose-500/50">
                <div className="relative aspect-[16/10] bg-zinc-950">
                  {m.thumbnail_url && <img src={m.thumbnail_url} alt={m.title} className="h-full w-full object-cover" />}
                  <div className="absolute left-3 top-3"><StatusBadge status={m.order_status} /></div>
                </div>
                <div className="space-y-3 p-5">
                  <MediaTypeBadges types={m.media_types} testidPrefix={`ev-badge-${m.id}`} />
                  <h3 className="font-display text-lg font-bold text-white">{m.title}</h3>
                  {(fields.name?.enabled || fields.number?.enabled || fields.studio?.enabled || fields.class?.enabled) && (
                    <div className="space-y-0.5 text-xs text-zinc-400">
                      {fields.name?.enabled && m.performer_name && <div><span className="text-zinc-600">{fields.name.label}:</span> {m.performer_name}</div>}
                      {fields.number?.enabled && m.competitor_number && <div><span className="text-zinc-600">{fields.number.label}:</span> {m.competitor_number}</div>}
                      {fields.studio?.enabled && m.studio && <div><span className="text-zinc-600">{fields.studio.label}:</span> {m.studio}</div>}
                      {fields.class?.enabled && m.class_name && <div><span className="text-zinc-600">{fields.class.label}:</span> {m.class_name}</div>}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2">
                    <span className="font-display text-xl font-extrabold text-white">{money(m.price)}</span>
                    {has(m.id) ? (
                      <Button data-testid={`in-cart-${m.id}`} onClick={() => navigate("/checkout")} variant="outline" className="border-emerald-500/40 bg-transparent text-emerald-400 hover:bg-emerald-500/10">
                        <Check className="mr-1.5 h-4 w-4" /> In cart
                      </Button>
                    ) : (
                      <Button data-testid={`event-buy-${m.id}`} onClick={() => { add({ media_item_id: m.id, title: m.title, price: m.price, thumbnail_url: m.thumbnail_url, content_type: m.content_type || (m.media_types || [])[0] || "", delivery_download: event.delivery_download, delivery_usb: event.delivery_usb, event_id: event.id, event_name: event.name, category: event.category, fields: event.fields, performance_mode: event.performance_mode, performances: event.performances }); toast.success("Added to cart"); }} disabled={!m.available} className="bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50] disabled:opacity-40">
                        <ShoppingCart className="mr-1.5 h-4 w-4" /> Add
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
